import { enumLogicalDeviceRole, enumOutputFileFormat } from '@ingenico/api-sdk-device-management-private-v2';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_CALL_LOGS } from '@ingenico/util-i18n-config';
import { Level, showNotification } from '@ingenico/react-notifications';
import { useBusinessServiceRequest } from '@ingenico/react-section-utils';
import { downloadFile } from '@ingenico/react-utils/functions';
import { isEmpty, toString } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { useDeviceManagementCapability, useDeviceManagementError } from '../../hooks';
import type {
  UseParameterOutputFileModalProps,
  UseParameterOutputFileModalReturn,
} from './ParameterOutputFileModal.model';
import { ParameterOutputFileModalLifecycle } from './ParameterOutputFileModal.model';
import { findTemplateById, formatOutputFileCode, getVisibleDevices } from './ParameterOutputFileModal.util';

export function useParameterOutputFileModal({
  deploymentUnitId,
  deviceId,
  configurationId,
  moduleItemField,
  isOpen = false,
}: UseParameterOutputFileModalProps): UseParameterOutputFileModalReturn {
  const { getErrorMessage } = useDeviceManagementError();
  const isDmMultiMerchantEnabled = useDeviceManagementCapability('deployments.isDmMultiMerchantEnabled');

  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_CALL_LOGS));

  const {
    isLoading: isLoadingParameterTemplates,
    data: dataParameterTemplates,
    isSuccess: isSuccessParameterTemplates,
    isError: isErrorParameterTemplates,
    error: errorParameterTemplates,
  } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) => {
        if (isEmpty(moduleItemField)) {
          return null;
        }
        if (!isEmpty(deploymentUnitId)) {
          return deviceManagementService.useParameterOutputFileModal(deploymentUnitId, moduleItemField);
        }
        if (!isEmpty(configurationId)) {
          return deviceManagementService.useParameterOutputFileModalFromDeviceDetails(configurationId, deviceId);
        }
        return null;
      },
      [configurationId, deploymentUnitId, deviceId, moduleItemField],
    ),
  );

  // List of all templates
  const templates = useMemo(() => {
    return dataParameterTemplates?.transform ?? [];
  }, [dataParameterTemplates?.transform]);

  /* -------------------------------------------------------------------------- */
  /*                      SELECTORS: TEMPLATE AND FILENAME                      */
  /* -------------------------------------------------------------------------- */
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedDeviceIndex, setSelectedDeviceIndex] = useState(0);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [outputFilename, setOutputFilename] = useState('');

  const onSelectOutputFilename = useCallback((filename: string) => {
    setOutputFilename(filename);
  }, []);
  const onDeviceSearchChange = useCallback((search: string) => {
    setDeviceSearch(search);
  }, []);
  const onSelectDevice = useCallback(
    (index: number) => {
      setSelectedDeviceIndex(index);
      setDeviceSearch('');
      const template = findTemplateById(templates, selectedTemplateId);
      const firstOutputFile = template?.devices[index]?.outputFiles[0];
      setOutputFilename(firstOutputFile?.fileName ?? '');
    },
    [selectedTemplateId, templates],
  );
  const onSelectTemplate = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);
      const currTemplate = findTemplateById(templates, templateId);
      const primaryDeviceIndex = currTemplate?.devices.findIndex(
        ({ logicalDeviceRoleSnapshot }) => logicalDeviceRoleSnapshot === enumLogicalDeviceRole.PRIMARY,
      );
      const initialDeviceIndex = primaryDeviceIndex !== undefined && primaryDeviceIndex >= 0 ? primaryDeviceIndex : 0;
      setSelectedDeviceIndex(initialDeviceIndex);
      setDeviceSearch('');
      setOutputFilename(currTemplate?.devices[initialDeviceIndex]?.outputFiles[0]?.fileName ?? '');
    },
    [templates],
  );

  /* -------------------------------------------------------------------------- */
  /*                 ON API SUCCESS - SETUP INITIAL TEMPLATE ID                 */
  /* -------------------------------------------------------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                 CURRENT SELECTION: TEMPLATE AND OUTPUT_FILE                */
  /* -------------------------------------------------------------------------- */
  const selectedTemplate = useMemo(() => {
    return isEmpty(selectedTemplateId) ? null : findTemplateById(templates, selectedTemplateId);
  }, [selectedTemplateId, templates]);

  const outputFiles = useMemo(() => {
    if (!selectedTemplate) {
      return [];
    }
    if (!isDmMultiMerchantEnabled) {
      return selectedTemplate.devices.flatMap(({ outputFiles: deviceOutputFiles }) => deviceOutputFiles);
    }
    return selectedTemplate.devices[selectedDeviceIndex]?.outputFiles ?? [];
  }, [isDmMultiMerchantEnabled, selectedDeviceIndex, selectedTemplate]);

  const devices = useMemo(() => {
    return selectedTemplate?.devices ?? [];
  }, [selectedTemplate?.devices]);

  const visibleDevices = useMemo(() => {
    return getVisibleDevices(devices, deviceSearch, selectedDeviceIndex);
  }, [deviceSearch, devices, selectedDeviceIndex]);

  /* -------------------------------------------------------------------------- */
  /*                API TO GET THE FILE CONTENT (Code, FILETYPE )               */
  /* -------------------------------------------------------------------------- */
  const {
    isLoading: isLoadingFileContent,
    data: dataFileContent,
    isSuccess: isSuccessFileContent,
    isError: isErrorFileContent,
    error: errorFileContent,
  } = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) => {
        if ([deploymentUnitId, selectedTemplateId, outputFilename].every((el) => !isEmpty(el))) {
          return deviceManagementService.downloadOutputFileForTemplate(
            deploymentUnitId,
            selectedTemplateId,
            outputFilename,
          );
        }

        if ([deviceId, selectedTemplate?.parameterSetId, outputFilename].every((el) => !isEmpty(el))) {
          return deviceManagementService.pureDownloadOutputFile(
            toString(deviceId),
            toString(selectedTemplate?.parameterSetId),
            outputFilename,
          );
        }

        return null;
      },
      [deploymentUnitId, deviceId, outputFilename, selectedTemplate?.parameterSetId, selectedTemplateId],
    ),
  );

  const { language, code } = useMemo(() => {
    if (!isSuccessFileContent) {
      return { language: '', code: '' };
    }
    const codeMemo = formatOutputFileCode(dataFileContent?.fileFormat, toString(dataFileContent?.content));

    return { language: dataFileContent?.fileFormat.toLowerCase(), code: codeMemo };
  }, [dataFileContent?.content, dataFileContent?.fileFormat, isSuccessFileContent]);

  const blobOptions = useMemo<BlobPropertyBag>(() => {
    return dataFileContent?.fileFormat === enumOutputFileFormat.XML
      ? { type: 'text/xml;charset=utf-8' }
      : { type: 'application/json' };
  }, [dataFileContent?.fileFormat]);

  const downloadOutputFile = useCallback(() => {
    if (dataFileContent) {
      downloadFile(new Blob([code], blobOptions), outputFilename);
    } else {
      showNotification({
        level: Level.Error,
        content: t('error.downloadFile', ALL_LNGS_NAMESPACES.COMMON),
      });
    }
  }, [dataFileContent, code, blobOptions, outputFilename, t]);

  const errorMessage = useMemo(() => {
    if (isErrorFileContent) {
      return getErrorMessage(
        errorFileContent,
        t('sections.parameterOutput.error.default', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS }),
      );
    }
    if (isErrorParameterTemplates) {
      return getErrorMessage(
        errorParameterTemplates,
        t('sections.parameterOutput.error.default', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS }),
      );
    }
    return t('sections.parameterOutput.error.default', { ns: ALL_LNGS_NAMESPACES.CALL_LOGS });
  }, [errorFileContent, errorParameterTemplates, getErrorMessage, isErrorFileContent, isErrorParameterTemplates, t]);

  const modalLifecycle = useMemo<ParameterOutputFileModalLifecycle>(() => {
    if (isLoadingParameterTemplates) {
      return ParameterOutputFileModalLifecycle.LoadingBoth;
    }
    if (isLoadingFileContent) {
      return ParameterOutputFileModalLifecycle.LoadingCode;
    }
    if (isErrorFileContent || isErrorParameterTemplates) {
      return ParameterOutputFileModalLifecycle.Error;
    }
    return ParameterOutputFileModalLifecycle.Success;
  }, [isErrorFileContent, isErrorParameterTemplates, isLoadingFileContent, isLoadingParameterTemplates]);

  useEffect(() => {
    if (isOpen) {
      if (isSuccessParameterTemplates && isEmpty(selectedTemplateId)) {
        const initialTemplate =
          templates.find(({ devices }) =>
            devices.some(
              ({ logicalDeviceRoleSnapshot }) => logicalDeviceRoleSnapshot === enumLogicalDeviceRole.PRIMARY,
            ),
          ) ?? templates[0];
        if (initialTemplate) {
          onSelectTemplate(initialTemplate.templateId);
        }
      }
    } else {
      setSelectedTemplateId('');
      setSelectedDeviceIndex(0);
      setDeviceSearch('');
      setOutputFilename('');
    }
  }, [isOpen, isSuccessParameterTemplates, onSelectTemplate, selectedTemplateId, templates]);

  return {
    isError: modalLifecycle === ParameterOutputFileModalLifecycle.Error,
    isSuccess: modalLifecycle === ParameterOutputFileModalLifecycle.Success,
    errorMessage,
    isLoading: modalLifecycle === ParameterOutputFileModalLifecycle.LoadingBoth,
    isLoadingCode: [
      ParameterOutputFileModalLifecycle.LoadingBoth,
      ParameterOutputFileModalLifecycle.LoadingCode,
    ].includes(modalLifecycle),
    code,
    language,
    moduleName: dataParameterTemplates?.configurationModule?.name ?? '',
    moduleVersion: moduleItemField?.version ?? '',
    devices,
    visibleDevices,
    deviceSearch,
    onDeviceSearchChange,
    onSelectOutputFilename,
    onSelectDevice,
    onSelectTemplate,
    outputFilename,
    outputFiles,
    selectedOutputFilename: outputFilename,
    selectedDeviceIndex,
    selectedTemplateId,
    templates,
    downloadOutputFile,
  };
}
